import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'

import FieldError from './FieldError'
import Fieldset from './Fieldset'
import Label from './Label'

const Switch = styled.div<{ disabled: boolean; compact: boolean }>`
	display: flex;
	position: relative;
	width: ${({ compact }) => (compact ? 32 : 44)}px;
	height: ${({ compact }) => (compact ? 20 : 24)}px;
	border-radius: ${({ compact }) => (compact ? 16 : 24)}px;
	background-color: ${({ theme }) => theme.colors.light.toggleBackground};
	will-change: background-color;
	transition: background-color 0.2s linear;
	cursor: pointer;
	outline-offset: 2px;

	${({ disabled }) =>
		disabled &&
		css`
			cursor: not-allowed;
		`}

	&::after {
		content: '';
		position: absolute;
		height: ${({ compact }) => (compact ? 14 : 16)}px;
		width: ${({ compact }) => (compact ? 14 : 16)}px;
		border-radius: ${({ theme }) => theme.borderRadiuses.round};
		top: ${({ compact }) => (compact ? 3 : 4)}px;
		left: ${({ compact }) => (compact ? 3 : 4)}px;
		background-color: ${({ theme }) => theme.colors.light.surface};
		will-change: left;
		transition: left 0.2s ease-in;
	}
`

const HiddenInput = styled.input<{ compact: boolean }>`
	position: absolute;
	display: none;

	&:checked + div {
		background-color: currentColor;

		&::after {
			left: ${({ compact }) => (compact ? 15 : 24)}px;
		}
	}
`

const ToggleLabel = styled(Label)`
	gap: 16px;
	display: flex;
	flex-direction: row;
	align-items: center;
`

type Props = JSX.IntrinsicElements['input'] & {
	label: string
	error?: string
	ariaLabel?: string
	compact?: boolean
}

const ToggleSwitch = React.forwardRef<HTMLInputElement, Props>(
	({ label, error, ariaLabel, className, disabled = false, compact = false, checked, ...restProps }: Props, ref) => {
		const id = React.useId()

		const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			restProps.onChange?.(event)
		}

		const handleLabelKeyUp = (event: React.KeyboardEvent<HTMLLabelElement>) => {
			if (event.code === 'Space' || event.code === 'Enter') {
				event.preventDefault()
				;(event.target as HTMLLabelElement).click()
			}
		}

		return (
			<Fieldset className={className}>
				<ToggleLabel htmlFor={id} onKeyUp={handleLabelKeyUp}>
					<span aria-hidden>{label}</span>
					<HiddenInput
						type="checkbox"
						disabled={disabled}
						{...restProps}
						checked={checked}
						onChange={handleInputChange}
						id={id}
						ref={ref}
						compact={compact}
					/>
					<Switch
						compact={compact}
						disabled={disabled}
						tabIndex={0}
						role="switch"
						aria-checked={checked}
						aria-disabled={disabled}
						aria-label={ariaLabel ? ariaLabel : label}
					/>
				</ToggleLabel>
				{error && <FieldError>{error}</FieldError>}
			</Fieldset>
		)
	},
)

ToggleSwitch.displayName = 'ToggleSwitch'

export default ToggleSwitch
